import { coreTypes } from "@core/core-types.di";
import {
    AssistantConversationCreatePayloadDto,
    AssistantConversationDto,
    AssistantConversationMessagePayloadDto,
    AssistantFeedbackPayloadDto,
    AssistantMessageDto,
    AssistantMessageExchangeDto,
} from "@core/data/dto/assistant.dto";
import type { Http } from "@core/data/infrastructures/http/http";
import { FallbackError } from "@core/domain/errors/fallback.error";
import { AssistantConversation } from "@core/domain/models/assistant-conversation.model";
import { AssistantMessage } from "@core/domain/models/assistant-message.model";
import { Either } from "@core/domain/types/either";
import { inject, injectable } from "inversify";
import { DateTime } from "luxon";

@injectable()
export class AssistantDatasource {
    constructor(
        @inject(coreTypes.infrastructure.Http) private readonly http: Http,
    ) {}

    async fetchConversations(): Promise<
        Either<FallbackError, AssistantConversation[]>
    > {
        const result = await this.http.get<
            | AssistantConversationDto[]
            | { results?: AssistantConversationDto[] }
        >("/assistant/");

        return result
            .mapLeft(() => new FallbackError())
            .map((response) => {
                const data = response.data as
                    | AssistantConversationDto[]
                    | { results?: AssistantConversationDto[] };
                const items = Array.isArray(data) ? data : data.results ?? [];

                return items.map((conversation) =>
                    this.mapConversation(conversation),
                );
            });
    }

    async fetchConversation(
        id: number,
    ): Promise<Either<FallbackError, AssistantConversation>> {
        const result = await this.http.get<AssistantConversationDto>(
            `/assistant/${id}/`,
        );

        return result
            .mapLeft(() => new FallbackError())
            .map((response) => this.mapConversation(response.data));
    }

    async createConversation(
        payload?: AssistantConversationCreatePayloadDto,
    ): Promise<Either<FallbackError, AssistantConversation>> {
        const result = await this.http.post<
            AssistantConversationDto,
            AssistantConversationCreatePayloadDto | undefined
        >("/assistant/", payload);

        return result
            .mapLeft(() => new FallbackError())
            .map((response) => this.mapConversation(response.data));
    }

    async deleteConversation(id: number): Promise<Either<FallbackError, true>> {
        const result = await this.http.delete(`/assistant/${id}/`);

        return result.mapLeft(() => new FallbackError()).map(() => true);
    }

    async sendMessage(
        conversationId: number,
        payload: AssistantConversationMessagePayloadDto,
    ): Promise<
        Either<
            FallbackError,
            {
                conversation: AssistantConversation;
                userMessage: AssistantMessage;
                assistantMessage: AssistantMessage;
            }
        >
    > {
        const result = await this.http.post<
            AssistantMessageExchangeDto,
            AssistantConversationMessagePayloadDto
        >(`/assistant/${conversationId}/messages/`, payload);

        return result
            .mapLeft(() => new FallbackError())
            .map((response) => ({
                conversation: this.mapConversation(response.data.conversation),
                userMessage: this.mapMessage(response.data.user_message),
                assistantMessage: this.mapMessage(
                    response.data.assistant_message,
                ),
            }));
    }

    async submitFeedback(
        messageId: number,
        payload: AssistantFeedbackPayloadDto,
    ): Promise<Either<FallbackError, AssistantMessage>> {
        const result = await this.http.post<
            AssistantMessageDto,
            AssistantFeedbackPayloadDto
        >(`/assistant_messages/${messageId}/feedback/`, payload);

        return result
            .mapLeft(() => new FallbackError())
            .map((response) => this.mapMessage(response.data));
    }

    private mapConversation(
        conversation: AssistantConversationDto,
    ): AssistantConversation {
        return new AssistantConversation(
            conversation.id,
            conversation.title,
            conversation.screen_context ?? null,
            DateTime.fromISO(conversation.created_at),
            DateTime.fromISO(conversation.updated_at),
            conversation.last_message_preview,
            (conversation.messages ?? []).map((message) =>
                this.mapMessage(message),
            ),
        );
    }

    private mapMessage(message: AssistantMessageDto): AssistantMessage {
        return new AssistantMessage(
            message.id,
            message.role,
            message.content,
            message.sources ?? [],
            message.metadata ?? null,
            message.feedback ?? null,
            message.feedback_comment ?? "",
            message.feedback_at ? DateTime.fromISO(message.feedback_at) : null,
            DateTime.fromISO(message.created_at),
        );
    }
}
